import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MiniCalendar from "./MiniCalendar";
import PopupSlotClient from "../../Component/popupSlotClient/popupSlotClient";
import "./PublicProfilePage.css";

/* ===== Base API robuste (prod & dev) ===== */
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE =
  RAW
    ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
    : (typeof window !== "undefined" && window.location.origin) || "";

/* ===== Helpers horaires (robustes ancien/nouveau format) ===== */
const toTime = (v) => {
  if (typeof v === "string" && v.includes(":")) return v;
  if (typeof v === "number" && !Number.isNaN(v)) {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  return "00:00";
};

const renderWorkingHours = (wh) => {
  if (!Array.isArray(wh) || wh.length === 0) return <li>Non renseigné</li>;
  return wh.map((h, i) => {
    const isOpen = !!h?.isOpen;
    const hasSlots = Array.isArray(h?.slots) && h.slots.length > 0;
    const slotsStr = hasSlots ? h.slots.map((s) => `${toTime(s?.start)} - ${toTime(s?.end)}`).join(" / ") : null;
    const legacyStr = h?.start || h?.end ? `${toTime(h?.start)} - ${toTime(h?.end)}` : null;

    return (
      <li key={i}>
        <b>{h?.day || `Jour ${i + 1}`} :</b> {isOpen ? slotsStr || legacyStr || "Ouvert" : "Fermé"}
      </li>
    );
  });
};

const PublicProfilePage = ({ isApp = false }) => {
  const [annonce, setAnnonce] = useState(null);
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [calendarKey, setCalendarKey] = useState(Date.now());
  const [mapUnlocked, setMapUnlocked] = useState(false);
  const [googleMapsKey, setGoogleMapsKey] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const searchParams = new URLSearchParams(location.search);
  const annonceId = searchParams.get("proId");
  const profilId = searchParams.get("profilId");

  const isRunningApp =
    isApp ||
    (typeof window !== "undefined" && (window?.Capacitor || document?.body?.classList?.contains("app")));

  useEffect(() => {
    fetch(`${API_BASE}/api/config/google-maps-key`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setGoogleMapsKey(d?.key || ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!annonceId) {
      setError("Aucun ID dans l’URL.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/annonces/${annonceId}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Erreur API : annonce non trouvée");
        return res.json();
      })
      .then((data) => {
        setAnnonce(data);
        if (profilId && data.profils && data.profils.length > 0) {
          const p = data.profils.find((pf) => String(pf._id) === String(profilId));
          setProfil(p || null);
        } else {
          setProfil(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Erreur réseau.");
        setLoading(false);
      });
  }, [annonceId, profilId, token]);

  const getProfessionList = () => {
    let professions = [];
    if (annonce) {
      if (Array.isArray(annonce.profils)) {
        professions = annonce.profils.map((p) => ({ label: p.profession, value: p._id }));
      }
      if (Array.isArray(annonce.professions) && annonce.professions.length > 0) {
        professions.unshift({ label: annonce.professions[0].profession, value: "" });
      }
    }
    return professions;
  };

  const handleProfessionChange = (e) => {
    const newProfilId = e.target.value;
    let url = `/pro/public-profile?proId=${annonceId}`;
    if (newProfilId) url += `&profilId=${newProfilId}`;
    navigate(url);
  };

  const handleSlotClick = (slotDate) => {
    setSelectedSlot(slotDate);
    setPopupVisible(true);
  };

  const handleSaveSlot = () => {
    setPopupVisible(false);
    setSelectedSlot(null);
    setCalendarKey(Date.now());
  };

  const handleClosePopup = () => {
    setPopupVisible(false);
    setSelectedSlot(null);
  };

  if (loading) return <div className="profilepro-loading">Chargement…</div>;
  if (error) return <div className="profilepro-error">{error}</div>;
  if (!annonce) return <div className="profilepro-error">Annonce non trouvée.</div>;

  const imageSrc = annonce?.image ? `${API_BASE}/uploads/${annonce.image}` : "/avatar-pro.png";
  const displayName = annonce?.nameCompany;
  const displayProfession = profil ? profil.profession : annonce?.professions && annonce.professions[0]?.profession;
  const displayCity = annonce?.city;
  const displayDesc = profil ? profil.description || annonce.description : annonce?.description;
  const displayPhone = annonce?.phoneNumber;
  const displayWorkHours = profil ? profil.workingHours : annonce?.workHours;

  let activities = [];
  if (profil && Array.isArray(profil.activities)) {
    activities = profil.activities;
  } else if (Array.isArray(annonce?.professions)) {
    activities = annonce.professions.flatMap((prof) => prof.activities || []);
  }

  const rootCls = `profilepro-root${isRunningApp ? " profilepro-root--app" : ""}`;
  const headerCls = `profilepro-header sticky-header${isRunningApp ? " profilepro-header--app" : ""}`;
  const headerLeftCls = `profilepro-header-left${isRunningApp ? " profilepro-header-left--app" : ""}`;
  const mainCls = `profilepro-main${isRunningApp ? " profilepro-main--app" : ""}`;
  const leftCls = `profilepro-main-left${isRunningApp ? " profilepro-main-left--app" : ""}`;
  const rightCls = `profilepro-main-right${isRunningApp ? " profilepro-main-right--app" : ""}`;
  const photoCls = `profilepro-photo${isRunningApp ? " profilepro-photo--app" : ""}`;
  const nameCls = `profilepro-name${isRunningApp ? " profilepro-name--app" : ""}`;
  const professionCls = `profilepro-profession${isRunningApp ? " profilepro-profession--app" : ""}`;
  const cityCls = `profilepro-city${isRunningApp ? " profilepro-city--app" : ""}`;
  const sectionCls = (extra = "") =>
    `profilepro-section${isRunningApp ? " profilepro-section--app" : ""}${extra ? " " + extra : ""}`;
  const summaryCardCls = `profilepro-summary-card${isRunningApp ? " profilepro-summary-card--app" : ""}`;

  const calendarComponent = (
    <MiniCalendar
      key={calendarKey}
      annonceId={annonce._id}
      profilId={profilId}
      onSlotClick={handleSlotClick}
      isApp={isRunningApp}
    />
  );

  return (
    <div className={rootCls}>
      <div className={headerCls}>
        <div className={headerLeftCls}>
          <img className={photoCls} src={imageSrc} alt={displayName} />
          <div>
            <h1 className={nameCls}>{displayName}</h1>
            {displayProfession ? <div className={professionCls}>{displayProfession}</div> : null}
            {displayCity ? <div className={cityCls}>{displayCity}</div> : null}
          </div>
        </div>
        <div style={{ marginLeft: 30 }}>
          <label style={{ fontWeight: 600, marginRight: 10 }}>Changer de profession :</label>
          <select value={profilId || ""} onChange={handleProfessionChange}>
            {getProfessionList().map((prof, idx) => (
              <option value={prof.value} key={idx}>
                {prof.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={mainCls}>
        <div className={leftCls}>
          {isRunningApp ? <div className={sectionCls()}>{calendarComponent}</div> : null}

          <div className={sectionCls()} id="presentation">
            <h3>Présentation</h3>
            <div>{displayDesc || "Présentation du professionnel..."}</div>
          </div>

          {!isRunningApp ? (
            <div className={sectionCls()} id="horaires">
              <h3>Horaires</h3>
              <ul>{renderWorkingHours(displayWorkHours)}</ul>
            </div>
          ) : null}

          <div className={sectionCls()} id="tarifs">
            <h3>Activités / Tarifs</h3>
            <ul>
              {activities && activities.length > 0 ? (
                activities.map((act, i) => (
                  <li key={i}>
                    {act.name} — {act.durationHours ?? 0}h{act.durationMinutes ? ` ${act.durationMinutes}min` : ""} —{" "}
                    {act.price}€
                  </li>
                ))
              ) : (
                <li>Non renseigné</li>
              )}
            </ul>
          </div>

          <div className={sectionCls()} id="localisation">
            <h3>Localisation</h3>
            <div className={`map-wrap${mapUnlocked ? " map-wrap--unlocked" : ""}`} aria-live="polite">
              {(() => {
                const coords = annonce.coordinates;
                const lat = Array.isArray(coords) && coords.length >= 2 ? parseFloat(coords[1]) : null;
                const lon = Array.isArray(coords) && coords.length >= 2 ? parseFloat(coords[0]) : null;
                const address = [annonce.adresCompany, annonce.postalCode, annonce.city].filter(Boolean).join(", ");

                let iframeSrc = "";
                if (lat != null && lon != null) {
                  if (googleMapsKey) {
                    iframeSrc = `https://www.google.com/maps/embed/v1/view?key=${googleMapsKey}&center=${lat},${lon}&zoom=15`;
                  } else {
                    const delta = 0.015;
                    iframeSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${lon - delta},${lat - delta},${lon + delta},${lat + delta}&layer=mapnik&marker=${lat},${lon}`;
                  }
                } else if (googleMapsKey && address) {
                  iframeSrc = `https://www.google.com/maps/embed/v1/place?key=${googleMapsKey}&q=${encodeURIComponent(address)}`;
                }

                return iframeSrc ? (
                  <>
                    <iframe
                      title="Carte de localisation"
                      className="map-frame"
                      width="100%"
                      height="250"
                      style={{ border: 0, marginTop: "10px" }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={iframeSrc}
                    />
                    {!mapUnlocked ? (
                      <button
                        type="button"
                        className="map-reveal-btn"
                        onClick={() => setMapUnlocked(true)}
                        aria-controls="localisation"
                        aria-expanded={mapUnlocked}
                      >
                        Afficher la carte
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p style={{ marginTop: 8, color: "var(--text-muted)" }}>
                    Coordonnées non disponibles. Utilisez le lien ci-dessous pour voir l&apos;adresse.
                  </p>
                );
              })()}
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                [annonce.adresCompany, annonce.postalCode, annonce.city].filter(Boolean).join(", ") || "France"
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="map-directions-link"
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 14,
                fontWeight: 600,
                color: "var(--terracotta)",
              }}
            >
              Voir l&apos;itinéraire →
            </a>
          </div>
        </div>

        <div className={rightCls}>
          <div className={summaryCardCls}>
            <h3>Informations</h3>
            <div>
              📍 {annonce.adresCompany}
              {annonce.postalCode ? ` ${annonce.postalCode}` : ""}
              {annonce.city ? `, ${annonce.city}` : ""}
            </div>
            <div style={{ marginTop: 10 }}>📞 {displayPhone}</div>
          </div>

          {!isRunningApp ? <div className={sectionCls()}>{calendarComponent}</div> : null}
        </div>
      </div>

      <PopupSlotClient
        visible={popupVisible}
        onClose={handleClosePopup}
        onSave={handleSaveSlot}
        slotTime={selectedSlot}
        annonceId={annonceId}
        profilId={profilId}
      />
    </div>
  );
};

export default PublicProfilePage;

